import React, { useState } from 'react'
import { useRouter } from 'next/router'
import {
  Box,
  Typography,
  InputAdornment,
  Skeleton,
  useMediaQuery,
} from '@mui/material'
import { styled, useTheme } from '@mui/material/styles'
import { SearchSVG } from 'components/common/SVGElements'
import TextFieldBasic from 'components/common/TextFieldBasic'
import { LS_APP_DOMAIN } from 'config/constants'
import { useHomePageSearchBarTitle } from 'utils/api/hooks'
import { GtmAgent } from 'utils/hooks/analytics'

const SearchBarSectionWrapper = styled(Box)(({ theme }) => ({
  position: 'relative',
  padding: '48px 0',
  display: 'flex',
  alignItems: 'center',
  [theme.breakpoints.up('lg')]: {
    padding: '72px 0',
  },
}))

const ContentWrapper = styled(Box)({
  margin: '0 auto',
  width: '100%',
  maxWidth: '1200px',
  zIndex: 1,
})

const TextWrapper = styled(Box)({
  maxWidth: '768px',
})

const ImageWrapper = styled(Box)(({ theme }) => ({
  position: 'absolute',
  right: 0,
  top: 0,
  width: '40%',
  maxWidth: '640px',
  height: '320px',
  zIndex: 0,
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    width: '40%',
    height: '100%',
    background: 'linear-gradient(to right, white 40%, transparent 100%)',
  },
  '&::after': {
    content: '""',
    position: 'absolute',
    bottom: 0,
    left: 0,
    width: '100%',
    height: '30%',
    background: 'linear-gradient(to top, white 20%, transparent 100%)',
  },
  [theme.breakpoints.down('lg')]: {
    display: 'none',
  },
}))

const StyledImage = styled('img')({
  width: '100%',
  height: '100%',
  objectFit: 'cover',
})

const SearchBarSection = () => {
  const [keyword, setKeyword] = useState('')
  const router = useRouter()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('lg'))

  const {
    data: homePageSearchBarTitle,
    isLoading: isHomePageSearchBarTitleLoading,
  } = useHomePageSearchBarTitle()

  const handleSearch = (e) => {
    if (e.key === 'Enter' && keyword.trim()) {
      const searchUrl = `${LS_APP_DOMAIN}/vender/search?keyword=${encodeURIComponent(
        keyword.trim()
      )}`
      GtmAgent.directSendToGTM({
        event: 'living_action',
        living_event_name: 'search_send',
        living_search_type: '廠商',
        living_search_keyword: `${keyword.trim()}`,
        living_search_location: '首頁',
      })
      router.push(searchUrl)
    }
  }

  return (
    <SearchBarSectionWrapper>
      <ContentWrapper px={{ xs: 4, md: 8, xl: 12 }}>
        <TextWrapper>
          <Typography
            variant={isMobile ? 'headlineMedium' : 'headlineLarge'}
            mb={6}
          >
            {isHomePageSearchBarTitleLoading ? (
              <Skeleton variant="rounded" width="70%" height="36px" />
            ) : (
              homePageSearchBarTitle || '讓居家幫你輕鬆過日子'
            )}
          </Typography>
          <TextFieldBasic
            fullWidth
            size="medium"
            placeholder="搜尋服務廠商"
            value={keyword}
            onChange={setKeyword}
            onKeyPress={handleSearch}
            InputProps={{
              startAdornment: (
                <InputAdornment>
                  <SearchSVG color={theme.palette.text.secondary} />
                </InputAdornment>
              ),
            }}
            sx={{
              '& .MuiInputBase-input': {
                backgroundColor: 'transparent',
              },
            }}
          />
        </TextWrapper>
      </ContentWrapper>
      <ImageWrapper>
        <StyledImage
          src="/img/img_search_bar_section_bg.webp"
          alt="Search bar background img"
        />
      </ImageWrapper>
    </SearchBarSectionWrapper>
  )
}

export default SearchBarSection
